import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getAccommodationsBlurPlaceholder } from "@/utils/accommodationsBlurPlaceholders";
import { Room } from "@/types/room";
import {
  Users,
  Square,
  Bed,
  Bath,
  ChevronLeft,
  ChevronRight,
  Mountain,
  Wifi,
  Tv,
  Wind,
  ChefHat,
  Shield,
  Star,
  Share2,
  Coffee,
  Sofa,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useViewportAutoplay } from "@/hooks/useViewportAutoplay";

interface JuniorSuiteSectionProps {
  room: Room;
  onViewDetails?: (roomId: string) => void;
  onQuickBook?: (roomId: string) => void;
}

const JuniorSuiteSection: React.FC<JuniorSuiteSectionProps> = ({
  room,
  onViewDetails,
  onQuickBook,
}) => {
  const { t } = useLanguage();

  // Viewport-based auto-play hook
  const {
    currentImageIndex,
    isAutoPlaying,
    elementRef,
    handlePreviousImage,
    handleNextImage,
    handleImageClick,
  } = useViewportAutoplay({
    imageCount: room.images.length,
    intervalMs: 4000,
    threshold: 0.7,
  });

  const handleViewDetails = () => {
    onViewDetails?.(room.id);
  };

  const handleQuickBook = () => {
    onQuickBook?.(room.id);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/accommodations#junior-suite`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${room.name} - Park Hotel Beatenberg`,
          text: `Check out our ${room.name} with comfortable accommodation and Alpine views`,
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard if share is cancelled
        copyToClipboard(url);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const currentImage = room.images[currentImageIndex];

  // Feature badges data
  const featureBadges = [
    {
      icon: Users,
      label: "Occupancy",
      value: t("rooms.juniorSuiteBadges.occupancy"),
      description: `${room.capacity.adults} adults, ${room.capacity.children} children`,
    },
    {
      icon: Square,
      label: "Room Size",
      value: t("rooms.juniorSuiteBadges.roomSize"),
      description: `${room.size.squareFeet} sq ft`,
    },
    {
      icon: Bed,
      label: "Bedding",
      value: t("rooms.juniorSuiteBadges.bedding"),
      description: "Premium bedding",
    },
    {
      icon: Bath,
      label: "Bathrooms",
      value: t("rooms.juniorSuiteBadges.bathrooms"),
      description: "Ensuite bathroom",
    },
  ];

  return (
    <section
      id="junior-suite"
      className="py-16 bg-gradient-to-b from-gray-50/50 to-white relative overflow-hidden"
      data-oid="sj7ug5w"
    >
      {/* Background Quadrilaterals - Mirrored positions */}
      <div
        className="absolute top-0 left-0 w-96 h-96 transform rotate-45 -translate-x-48 -translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="eew48jh"
      ></div>
      <div
        className="absolute bottom-0 right-0 w-96 h-96 transform rotate-45 translate-x-48 translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="0:kipgp"
      ></div>
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        data-oid="irhwazo"
      >
        {/* Mobile Header - Shown only on mobile */}
        <div className="mb-8 lg:hidden" data-oid="7_h10iu">
          <div className="mb-4" data-oid="zehuba5">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-playfair text-gray-900"
              data-oid="y.0.tsr"
            >
              {t("rooms.juniorSuite")}
            </h2>
          </div>

          {/* Key badges below title - Mobile */}
          <div
            className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap"
            data-oid="-14n1gm"
          >
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="oose1nk"
            >
              <Users
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="zg6lmw8"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="66osk6x"
              >
                {featureBadges[0].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="69lk6j6"
            >
              <Square
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="y:dx7xr"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="r9i9fm:"
              >
                {featureBadges[1].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="7rps.l6"
            >
              <Bed
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="rcz.xib"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="ybr3h65"
              >
                {featureBadges[2].value}
              </span>
            </div>
          </div>
        </div>

        {/* 2-Column Layout: Content Left (40-50%), Carousel Right (50-60%) - MIRRORED */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[50%_50%] xl:grid-cols-[45%_55%] 2xl:grid-cols-[40%_60%] gap-6 lg:gap-8 xl:gap-12 mb-12"
          data-oid="1j0zaph"
        >
          {/* Left Column - Content */}
          <div
            className="flex flex-col gap-8 min-w-0 overflow-hidden pl-4 lg:pl-6 xl:pl-8 order-2 lg:order-1"
            data-oid="2-9epb1"
          >
            {/* Desktop Header - Shown only on desktop */}
            <div className="hidden lg:block" data-oid="5-les.v">
              <div className="mb-4" data-oid="ge870ap">
                <h2
                  className="text-3xl xl:text-4xl 2xl:text-5xl font-playfair text-gray-900"
                  data-oid="_eqxpkp"
                >
                  {t("rooms.juniorSuite")}
                </h2>
              </div>

              {/* Key badges below title - Desktop */}
              <div
                className="flex items-center gap-2 lg:gap-3 xl:gap-4 2xl:gap-6 flex-wrap"
                data-oid="cyzvege"
              >
                <div className="flex items-center gap-2" data-oid="a-y2wot">
                  <Users
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="00nubiz"
                  />

                  <span className="text-sm text-gray-900" data-oid="iqkfc:8">
                    {featureBadges[0].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid=":4nb5_e">
                  <Square
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="iayyrlo"
                  />

                  <span className="text-sm text-gray-900" data-oid="7:43jnc">
                    {featureBadges[1].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="m7faul9">
                  <Bed
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="3lsohet"
                  />

                  <span className="text-sm text-gray-900" data-oid="csbsfje">
                    {featureBadges[2].value}
                  </span>
                </div>
              </div>
            </div>

            {/* 1. Description */}
            <div className="overflow-hidden" data-oid="9mtc11f">
              <p
                className="text-sm lg:text-base xl:text-lg text-gray-700 leading-relaxed break-words"
                data-oid="9.0ufsm"
              >
                <span className="sm:hidden" data-oid="4g0r2fw">
                  Elegant 35m² suite with king bed, perfect for couples seeking
                  comfort and style. Premium accommodation with stunning views
                  of Lake Thun, Eiger, Mönch, Jungfrau, and Beatenberg village.
                </span>
                <span className="hidden sm:inline" data-oid="0_yk.8e">
                  {t("rooms.juniorSuiteDescription.desktop")}
                </span>
              </p>
            </div>

            {/* 2. All Features & Amenities - Consolidated */}
            <div className="grid grid-cols-1 gap-y-4" data-oid="1b21-jl">
              {/* Lounge Seating */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="bm_2y8:"
              >
                <Sofa
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="mhszs-1"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="mz6c57o"
                >
                  {t("rooms.juniorSuiteFeatures.loungeSeating")}
                </span>
              </div>

              {/* Private Balcony with Views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="jpluq92"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="e-a0o1s"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="4jfde5x"
                >
                  {t("rooms.juniorSuiteFeatures.privateBalconyWithViews")}
                </span>
              </div>

              {/* Space for Additional Beds */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="-:vevbe"
              >
                <Bed
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="dojviw6"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="hks:eue"
                >
                  {t("rooms.juniorSuiteFeatures.spaceForAdditionalBeds")}
                </span>
              </div>

              {/* Free Wifi */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid=":0nmp7i"
              >
                <Wifi
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="f8l6f5c"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="30xgv46"
                >
                  Free Wifi
                </span>
              </div>

              {/* Premium organic bio cotton bedding */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="9ly:w15"
              >
                <Star
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="x7xkfw5"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="4m7.6b0"
                >
                  Premium organic bio cotton bedding
                </span>
              </div>

              {/* Ensuite coffee machine */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="i1upz0u"
              >
                <Coffee
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="ws-yjz2"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="ikxrhic"
                >
                  Ensuite coffee machine
                </span>
              </div>
            </div>

            {/* 3. Book Now and Share Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              data-oid="fjpr-8:"
            >
              <Button
                asChild
                size="lg"
                className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                trackingType="booking"
                trackingLabel={t("nav.bookNow")}
                trackingLocation="Junior Suite Section"
                data-oid="k0_-sqk"
              >
                <a
                  href={import.meta.env.VITE_BOOKING_URL}
                  data-oid="vhdcymj"
                >
                  {t("nav.bookNow")}
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="bg-white/80 hover:bg-alpine-gold hover:text-white border-gray-300 hover:border-alpine-gold transition-all duration-300 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105"
                onClick={handleShare}
                aria-label="Share Junior Suite"
                data-oid=".n-6hxz"
              >
                <Share2 className="w-4 h-4 mr-2" data-oid="alhgcwe" />
                {t("common.share")}
              </Button>
            </div>
          </div>

          {/* Right Column - Image Carousel */}
          <div
            ref={elementRef}
            className="relative w-full aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden rounded-2xl shadow-2xl order-1 lg:order-2"
            data-oid="j6n:umz"
          >
            <div
              className="flex h-full transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              data-oid="xtg8wub"
            >
              {room.images.map((image, index) => (
                <div
                  key={image.id}
                  className="w-full h-full flex-shrink-0"
                  data-oid="wer9a-q"
                >
                  <OptimizedImage
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    priority={false}
                    rootMargin={index === 0 ? "300px" : "200px"}
                    blurDataURL={getAccommodationsBlurPlaceholder(image.url)}
                    responsive={true}
                    data-oid="ljum60v"
                  />
                </div>
              ))}
            </div>

            {/* Image Navigation */}
            {room.images.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handlePreviousImage}
                  aria-label="Previous image"
                  data-oid="lk8t1hc"
                >
                  <ChevronLeft className="w-5 h-5" data-oid="axdd_:x" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  data-oid="d-oueq0"
                >
                  <ChevronRight className="w-5 h-5" data-oid="4r_nfhf" />
                </Button>
              </>
            )}

            {/* Image Indicators */}
            {room.images.length > 1 && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
                data-oid=".l:ei5y"
              >
                {room.images.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all duration-300",
                      index === currentImageIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/75",
                    )}
                    onClick={() => handleImageClick(index)}
                    aria-label={`Go to image ${index + 1}`}
                    data-oid="o124jbc"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default JuniorSuiteSection;
